import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./Home.css";

import personalInjuryIcon from "./assets/personal_injury_icon.png";

import attorneyPhotoCircle from "./assets/edgar-lopez-law.png";
import attorneyPhotoWide from "./assets/edgar-lopez-law-1.png";

function Home() {
  const pageRef = useRef(null);

  useEffect(() => {
    // Reveal on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
          else entry.target.classList.remove("visible");
        });
      },
      { threshold: 0.2 }
    );

    const sections = document.querySelectorAll(".reveal");
    sections.forEach((sec) => observer.observe(sec));

    // Parallax vars (mobile-friendly): expose scroll position to CSS
    const el = pageRef.current;
    let ticking = false;

    const setVars = () => {
      if (!el) return;
      el.style.setProperty("--scrollY", String(window.scrollY || 0));
      el.style.setProperty("--vh", String(window.innerHeight || 0));
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(setVars);
      }
    };

    // init + listeners
    setVars();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", setVars);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", setVars);
    };
  }, []);

  return (
    <div className="home-container" ref={pageRef}>
      <Helmet>
        <title>Edgar Lopez Law | Personal Injury Attorney</title>
        <meta
          name="description"
          content="Edgar Lopez Law is a personal injury law office representing accident and injury victims. Explore our practice focus, meet the attorney, and request a free consultation."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section">
        <img
          className="hero-video"
          src="/banner.png"
          alt="Edgar Lopez Banner"
        />
        
      </section>

      {/* Hero Bridge Video (fills mobile gap) */}
      <section className="hero-bridge" aria-hidden="true">
        <video className="hero-bridge-video" autoPlay loop muted playsInline>
          <source src="/bridge.mp4" type="video/mp4" />
          Your browser does not support HTML5 video.
        </video>
      </section>

      {/* Practice Areas */}
      <section className="practice-areas-section reveal">
        <h2 className="practice-areas-title">Our Practice Areas</h2>

        <div className="practice-areas-grid">
          <Link to="/about#car-accidents" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Car Accidents"
              />
              <h3 className="practice-card-title">CAR ACCIDENTS</h3>
              <p className="practice-card-text">
                If you were injured in a crash, we help you pursue compensation
                for medical bills, lost income, and pain and suffering—while
                dealing with the insurance company so you can focus on recovery.
              </p>
            </article>
          </Link>

          <Link to="/about#motorcycle-accidents" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Motorcycle Accidents"
              />
              <h3 className="practice-card-title">MOTORCYCLE ACCIDENTS</h3>
              <p className="practice-card-text">
                Motorcycle injuries are often serious. We build strong cases with
                clear documentation and advocacy to protect riders from unfair
                blame and lowball offers.
              </p>
            </article>
          </Link>

          <Link to="/about#truck-accidents" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Truck Accidents"
              />
              <h3 className="practice-card-title">TRUCK ACCIDENTS</h3>
              <p className="practice-card-text">
                Truck accident claims often involve severe injuries, multiple
                parties, and commercial insurance issues. We help investigate the
                facts, preserve evidence, and pursue the compensation you may be
                entitled to recover.
              </p>
            </article>
          </Link>

          <Link to="/about#dog-bites" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Dog Bites"
              />
              <h3 className="practice-card-title">DOG BITES</h3>
              <p className="practice-card-text">
                Dog bite injuries can leave lasting physical and emotional harm.
                We help clients document the incident, protect their rights, and
                pursue recovery for medical treatment, scarring, and other damages.
              </p>
            </article>
          </Link>

          <Link to="/about#slip-falls" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Slip and Fall"
              />
              <h3 className="practice-card-title">SLIP &amp; FALLS</h3>
              <p className="practice-card-text">
                Property owners have a duty to keep premises reasonably safe. If
                dangerous conditions caused your fall, we investigate quickly and
                preserve evidence to support your claim.
              </p>
            </article>
          </Link>

          <Link to="/about#rideshare" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Rideshare Accidents"
              />
              <h3 className="practice-card-title">RIDESHARE (UBER / LYFT)</h3>
              <p className="practice-card-text">
                Rideshare claims can involve multiple insurance policies. Whether
                you were a passenger, driver, or hit by a rideshare vehicle, we
                help identify coverage and pursue the right parties.
              </p>
            </article>
          </Link>

          <Link to="/about#pedestrian-accidents" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Pedestrian Accidents"
              />
              <h3 className="practice-card-title">PEDESTRIAN ACCIDENTS</h3>
              <p className="practice-card-text">
                Pedestrians are especially vulnerable in traffic collisions. If
                you or a loved one was hit by a vehicle, we help build a strong
                claim supported by treatment records, evidence, and clear advocacy.
              </p>
            </article>
          </Link>
          <Link to="/about#civil-rights" className="practice-card-link">
            <article className="practice-card">
              <img
                className="practice-icon"
                src={personalInjuryIcon}
                alt="Civil Rights"
              />
              <h3 className="practice-card-title">CIVIL RIGHTS</h3>
              <p className="practice-card-text">
                Civil rights cases can involve serious misconduct, abuse of power,
                or violations of constitutional protections. We help clients build
                strong claims supported by evidence, documentation, and focused
                advocacy.
              </p>
            </article>
          </Link>
        </div>

        <div className="practice-areas-callout">
          <span className="practice-areas-callout-label">GIVE US A CALL</span>
          <a className="practice-areas-callout-phone" href="tel:+19517492948">
            (951) 749-2948
          </a>
        </div>
      </section>

      {/* Attorney Section */}
      <section className="attorney-section reveal">
        <h2 className="attorney-title">Attorney Edgar Lopez</h2>

        <div className="attorney-grid">
          <div className="attorney-photo-wrap">
            <img
              src={attorneyPhotoCircle}
              alt="Attorney Edgar Lopez"
              className="attorney-photo-circle"
            />
          </div>

          <div className="attorney-bio">
            <p>
              Edgar Lopez is a distinguished graduate of California Western School
              of Law in San Diego, California, and the founder of Edgar Lopez Law.
              He built his practice with a clear mission: to advocate fiercely for
              people who need a strong and dedicated voice on their side.
            </p>
            <p>
              Although his legal career began in defense, it quickly became clear
              that his true calling was representing injured individuals and helping
              them pursue justice. That realization led him to establish a practice
              focused exclusively on Personal Injury Law.
            </p>
            <p>
              Serving the Inland community and surrounding areas, Edgar Lopez is
              committed to helping clients seek the best possible outcome in a wide
              range of injury matters, including car accidents, Uber accidents,
              truck accidents, dog bites, slip and falls, and pedestrian accidents.
            </p>
            <p>
              When you choose Edgar Lopez Law, you gain a dedicated advocate who
              works relentlessly to pursue the compensation you may be owed.
            </p>
            <p>El abogado Edgar Lopez habla español con fluidez.</p>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="core-values-section reveal">
        <div className="core-values-grid">
          <div className="core-values-photo-wrap">
            <img
              src={attorneyPhotoWide}
              alt="Edgar Lopez Law"
              className="core-values-photo"
            />
          </div>

          <div className="core-values-content">
            <div className="lang-toggle">Español | English</div>
            <h2 className="core-values-title">Why Choose Edgar Lopez Law?</h2>
            <p className="core-values-text">
              When you are facing a personal injury matter, the right legal
              representation can make all the difference. At Edgar Lopez Law, we
              offer more than legal service—we offer dedicated advocacy focused on
              helping clients pursue the best possible outcome. Our firm is built on
              personal attention, strong communication, and a focused commitment to
              Personal Injury Law.
            </p>

            <a className="core-values-btn" href="/personal-injury">
              Learn More
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;